<template>
    <div>
        <div v-if="!jobId" class="alert alert-secondary">
            <i class="fas fa-info-circle me-2"></i>
            Save the job first to configure the issue reporter widget.
        </div>

        <template v-else>
            <!-- Header -->
            <div class="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h6 class="mb-1">
                        <Code :size="18" class="me-2" style="vertical-align: text-bottom;" />
                        Issue Reporter Widget
                    </h6>
                    <small class="text-muted">
                        Embed this widget on any external site to let users report issues directly to this job.
                    </small>
                </div>
                <button
                    class="btn btn-sm btn-primary"
                    :disabled="saving"
                    @click="saveSettings"
                >
                    <i class="fas fa-save me-1"></i>
                    {{ saving ? 'Saving…' : 'Save Settings' }}
                </button>
            </div>

            <!-- Loading state -->
            <div v-if="loading" class="text-center py-4 text-muted">
                <i class="fas fa-spinner fa-spin me-2"></i> Loading widget settings…
            </div>

            <template v-else>
                <!-- Enable toggle -->
                <div class="card mb-3">
                    <div class="card-body py-2">
                        <div class="form-check form-switch">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="widgetEnabled"
                                v-model="settings.Enabled"
                            />
                            <label class="form-check-label fw-semibold" for="widgetEnabled">
                                Widget Enabled
                            </label>
                        </div>
                        <small class="text-muted d-block mt-1">
                            When disabled the widget hides itself on embedded sites automatically.
                        </small>
                    </div>
                </div>

                <!-- Auth mode -->
                <div class="card mb-3">
                    <div class="card-header py-2 fw-semibold" style="font-size: 0.85rem;">
                        <ShieldCheck :size="14" class="me-1" style="vertical-align: text-bottom;" />
                        Authentication Mode
                    </div>
                    <div class="card-body py-2">
                        <div class="form-check mb-1">
                            <input
                                class="form-check-input"
                                type="radio"
                                id="authPublic"
                                value="public"
                                v-model="settings.AuthMode"
                            />
                            <label class="form-check-label" for="authPublic">
                                <strong>Public (anonymous)</strong> — anyone can submit issues without logging in.
                            </label>
                        </div>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="radio"
                                id="authAuthenticated"
                                value="authenticated"
                                v-model="settings.AuthMode"
                                disabled
                            />
                            <label class="form-check-label text-muted" for="authAuthenticated">
                                <strong>Authenticated</strong> — not available for cross-site embeds yet.
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Notifications -->
                <div class="card mb-3">
                    <div class="card-header py-2 fw-semibold" style="font-size: 0.85rem;">
                        <Bell :size="14" class="me-1" style="vertical-align: text-bottom;" />
                        Notifications
                    </div>
                    <div class="card-body py-2">
                        <div class="form-check form-switch">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="notifyOwner"
                                v-model="settings.NotifyOwner"
                            />
                            <label class="form-check-label" for="notifyOwner">
                                Notify the job owner when a new issue is submitted
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Embed snippet -->
                <div class="card mb-3" v-if="settings.Token">
                    <div class="card-header py-2 fw-semibold d-flex align-items-center justify-content-between" style="font-size: 0.85rem;">
                        <span>
                            <Code :size="14" class="me-1" style="vertical-align: text-bottom;" />
                            Embed Script
                        </span>
                        <button
                            class="btn btn-sm btn-outline-secondary"
                            @click="copySnippet"
                            title="Copy embed code"
                        >
                            <Copy :size="13" class="me-1" style="vertical-align: text-bottom;" />
                            Copy
                        </button>
                    </div>
                    <div class="card-body py-2">
                        <pre class="bg-light rounded p-2 mb-2" style="font-size: 0.78rem; white-space: pre-wrap; word-break: break-all;">{{ embedSnippet }}</pre>
                        <small class="text-muted">
                            Paste this <code>&lt;script&gt;</code> tag anywhere in the external site's HTML, just before <code>&lt;/body&gt;</code>.
                        </small>
                    </div>
                </div>

                <!-- Regenerate token -->
                <div class="card mb-3" v-if="settings.Token">
                    <div class="card-header py-2 fw-semibold text-danger" style="font-size: 0.85rem;">
                        <RefreshCw :size="14" class="me-1" style="vertical-align: text-bottom;" />
                        Danger Zone
                    </div>
                    <div class="card-body py-2">
                        <p class="mb-2 text-muted" style="font-size: 0.85rem;">
                            Regenerating the token invalidates the current embed script. Any sites using the old snippet will stop working until you update them.
                        </p>
                        <button
                            class="btn btn-sm btn-outline-danger"
                            :disabled="regenerating"
                            @click="confirmRegenerate"
                        >
                            <RefreshCw :size="13" class="me-1" style="vertical-align: text-bottom;" />
                            {{ regenerating ? 'Regenerating…' : 'Regenerate Token' }}
                        </button>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Code, ShieldCheck, Bell, RefreshCw, Copy } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { getApiBaseUrl } from '@/services/Http';
import { useApiRequest } from '@/composables';

const props = defineProps({
    jobId: {
        type: Number,
        default: null
    }
});

const { state: loadState, execute: executeLoad } = useApiRequest('widget:load');
const { state: saveState, execute: executeSave } = useApiRequest('widget:save');
const { state: regenerateState, execute: executeRegenerate } = useApiRequest('widget:regenerate');
const loading = computed(() => loadState.value.IsLoading);
const saving = computed(() => saveState.value.IsLoading);
const regenerating = computed(() => regenerateState.value.IsLoading);

const settings = ref({
    JobId: props.jobId,
    Enabled: true,
    AuthMode: 'public',
    NotifyOwner: true,
    Token: null
});

const embedSnippet = computed(() => {
    if (!settings.value.Token) return '';
    const base = getApiBaseUrl().replace('/v1', '');
    const schema = settings.value.TenantSchema ? ` data-schema="${settings.value.TenantSchema}"` : '';
    return `<script src="${base}/widget/radar-widget.js" data-token="${settings.value.Token}"${schema}><\/script>`;
});

const loadSettings = async () => {
    if (!props.jobId) return;
    await executeLoad(() => ApiService.getWidgetSettings(props.jobId));
    if (loadState.value.IsError) {
        Alert.notify({ text: loadState.value.Message || 'Failed to load widget settings.', type: 'error' });
        return;
    }
    if (loadState.value.Data) {
        settings.value = { ...loadState.value.Data };
    } else {
        settings.value = {
            JobId: props.jobId,
            Enabled: true,
            AuthMode: 'public',
            NotifyOwner: true,
            Token: null
        };
    }
};

const saveSettings = async () => {
    if (!props.jobId) {
        Toast.warning('Save the job first to configure the widget.');
        return;
    }

    const payload = {
        JobId: props.jobId,
        Enabled: settings.value.Enabled,
        AuthMode: 'public',
        NotifyOwner: settings.value.NotifyOwner
    };
    await executeSave(() => ApiService.saveWidgetSettings(payload));
    if (saveState.value.IsError) {
        Alert.notify({ text: saveState.value.Message || 'Failed to save widget settings.', type: 'error' });
        return;
    }
    if (saveState.value.Data) {
        settings.value = { ...saveState.value.Data };
        Toast.success('Widget settings saved.');
    }
};

const copySnippet = () => {
    if (!embedSnippet.value) return;
    navigator.clipboard.writeText(embedSnippet.value).then(() => {
        Toast.success('Embed snippet copied to clipboard.');
    }).catch(() => {
        Toast.warning('Could not copy to clipboard.');
    });
};

const confirmRegenerate = () => {
    Alert.confirm(
        'Regenerating the token will break any existing embed scripts. Are you sure?',
        async () => {
            await executeRegenerate(() => ApiService.regenerateWidgetToken(props.jobId));
            if (regenerateState.value.IsError) {
                Alert.notify({ text: regenerateState.value.Message || 'Failed to regenerate widget token.', type: 'error' });
                return;
            }
            if (regenerateState.value.Data) {
                settings.value = { ...regenerateState.value.Data };
                Toast.success('Token regenerated. Update your embed script.');
            }
        }
    );
};

watch(() => props.jobId, (val) => {
    if (val) loadSettings();
}, { immediate: false });

onMounted(() => {
    if (props.jobId) loadSettings();
});
</script>
